```vue
<template>
  <div class="authWrapper new-login-wrapper">
    <div class="container">
      <div class="row whiteBG-wrap login-white-wrap">
        <div class="col-lg-7 col-md-7 no-padding">
          <div class="login-left-wrap">
            <router-link to="/verify-identity" class="text-link">
              <h6 class="login-highlight">Back</h6>
            </router-link>

            <div class="left-login d-flex">
              <div class="rep-icon-login">
                <img src="../../assets/images/member-login.svg" alt="Company Logo" />
              </div>
            </div>

            <h4 class="login-title mb-2">Verify OTP</h4>

            <div class="auth-form pt-1 pb-3">
              <form @submit.prevent="verifyToken">
                <label class="opt-label mb-2 d-block">
                  Enter OTP<span class="required-sign"> *</span>
                </label>

                <div class="otp-input-wrapper d-flex">
                  <input
                    v-for="(_, index) in 6"
                    :key="index"
                    ref="inputs"
                    v-model="digits[index]"
                    type="tel"
                    maxlength="1"
                    autocomplete="one-time-code"
                    class="otp-input"
                    :class="{ 'highlight_otp': otpErrorFlag }"
                    :disabled="isLockedOut"
                    @input="handleInput(index, $event)"
                    @keydown.delete="handleDelete(index, $event)"
                    @paste.prevent="handlePaste"
                    @focus="clearError"
                  />
                </div>

                <div v-if="otpErrorFlag" class="valid-error text-center mt-2" style="width: 100%;">
                  {{ errorMsg }}
                </div>

                <div class="text-center mt-3">
                  <button 
                    type="submit"
                    class="submit-btn next-btn"
                    :disabled="!isOtpComplete || formLoader || isLockedOut"
                  >
                    <b-spinner v-if="formLoader" small />
                    {{ formLoader ? 'Verifying...' : 'Verify OTP' }}
                  </button>
                </div>
              </form>

              <login-otp 
                v-if="!isLockedOut"
                @sendOTP="sendOTP" 
                :expirySecond="60" 
                :setSpinnerLoader="otpLoader" 
              />
              <lockout-timer 
                v-if="isLockedOut && lockoutSeconds > 0"
                :lockout-seconds="lockoutSeconds"
                @lockout-expired="handleLockoutExpired"
              />
            </div>
          </div>
        </div>

        <div class="col-lg-5 col-md-5 no-padding d-none d-md-block">
          <login-in-carsoule />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import helper from "@/resource/Helper";
import Logincarsoule from "./Logincarsoule";
import NewLoginOtp from "./NewLoginOtp.vue";
import LockoutTimer from "./OtpLockoutTimer.vue";
import Api from "@/resource/MemberApi";

export default {
  name: "OtpVerify",
  components: {
    'login-in-carsoule': Logincarsoule,
    'login-otp': NewLoginOtp,
    'lockout-timer': LockoutTimer
  },
  data: () => ({
    digits: Array(6).fill(''),
    userEmail: '',
    formLoader: false,
    otpLoader: false,
    otpErrorFlag: false,
    errorMsg: '',
    isLockedOut: false,
    lockoutSeconds: 0
  }),
  computed: {
    ...mapGetters(['verifiedIdentitySelectedInfo', 'deviceId']),
    isOtpComplete() {
      return this.digits.every(d => d.length === 1);
    },
    otp() {
      return this.digits.join('');
    }
  },
  mounted() {
    this.userEmail = this.$store.getters.memberInfoVerifyIdentity?.email || '';
    this.focusFirstInput();
  },
  methods: {
    handleInput(index, event) {
      if (this.isLockedOut) return;
      const value = event.target.value.replace(/\D/g, '');
      this.digits.splice(index, 1, value.slice(-1));
      if (value && index < 5) {
        this.$refs.inputs[index + 1].focus();
      }
      if (this.isOtpComplete) this.verifyToken();
    },
    handleDelete(index) {
      if (this.isLockedOut) return;
      if (index > 0 && !this.digits[index]) {
        this.$refs.inputs[index - 1].focus();
      }
    },
    handlePaste(event) {
      if (this.isLockedOut) return;
      const pasteData = (event.clipboardData || window.clipboardData)
        .getData('text/plain')
        .replace(/\D/g, '')
        .slice(0, 6)
        .split('');
      this.digits = [...pasteData, ...Array(6 - pasteData.length).fill('')];
      this.focusInput(pasteData.length);
      if (this.isOtpComplete) this.verifyToken();
    },
    async verifyToken() {
      if (!this.isOtpComplete || this.isLockedOut) return;
      this.formLoader = true;
      this.clearError();
      try {
        const deviceId = this.deviceId || this.$uuid.v1();
        if (!this.deviceId) this.$store.commit("setDeviceId", deviceId);

        const payload = {
          email: this.userEmail,
          device_id: deviceId,
          reset_code: this.otp
        };

        const response = await Api.post('v4/auth/reset-verify-otp', null, { params: payload });

        if (response.data.status === "success") {
          this.$store.commit("setOtpCode", this.otp);
          this.$store.commit("setResetToken", response.data.reset_token);
          this.$store.commit("setResetTokenExpiry", response.data.expires_at);
          this.$router.push("/reset-password");
        }
      } catch (error) {
        const errorResponse = error.response?.data;
        if (errorResponse?.status === 429 || errorResponse?.statusCode === 429) {
          this.isLockedOut = true;
          this.lockoutSeconds = this.parseLockoutDuration(errorResponse?.message || '60 seconds');
          this.otpErrorFlag = false;
          this.errorMsg = '';
        } else {
          const msg = errorResponse?.message || "Something went wrong. Please try again.";
          this.setOtpError(msg);
        }
      } finally {
        this.formLoader = false;
      }
    },
    focusFirstInput(retry = 5) {
      this.$nextTick(() => {
        const input = this.$refs.inputs?.[0];
        if (input) {
          input.focus();
        } else if (retry > 0) {
          setTimeout(() => this.focusFirstInput(retry - 1), 50);
        }
      });
    },
    focusInput(index) {
      if (this.isLockedOut) return;
      this.$nextTick(() => {
        const target = this.$refs.inputs?.[index];
        if (target) target.focus();
      });
    },
    setOtpError(msg) {
      this.otpErrorFlag = true;
      this.errorMsg = msg;
    },
    clearError() {
      this.otpErrorFlag = false;
      this.errorMsg = '';
    },
    parseLockoutDuration(message) {
      console.log('Parsing lockout duration:', message);
      if (!message) {
        console.warn('No message provided, using default duration of 60 seconds');
        return 60;
      }
      const secondsMatch = message.match(/Try again after (\d+) seconds/);
      if (secondsMatch && secondsMatch[1]) {
        const parsedSeconds = parseInt(secondsMatch[1], 10);
        console.log('Parsed as seconds:', parsedSeconds);
        return parsedSeconds;
      }
      const match = message.match(/(\d+)\s*minute[s]?(\s*and\s*(\d+)\s*second[s]?)?|(\d+)\s*second[s]?/);
      if (!match) {
        console.warn('Failed to parse lockout duration, using default of 60 seconds:', message);
        return 60;
      }
      const minutes = parseInt(match[1]) || 0;
      const seconds = parseInt(match[3]) || parseInt(match[4]) || 0;
      const totalSeconds = minutes * 60 + seconds;
      console.log('Parsed duration:', { minutes, seconds, totalSeconds });
      return totalSeconds;
    },
    async sendOTP() {
      if (this.isLockedOut) return;
      this.otpLoader = true;
      this.$store.dispatch('verifiedIdentitySelectedInfo', this.verifiedIdentitySelectedInfo);
      try {
        const response = await Api.post('v4/auth/reset-generate-otp', this.verifiedIdentitySelectedInfo);
        if (response.data.status === "success") {
          this.$router.push('/verify-otp');
          helper.infoMessage(this, response.data.status, response.data.message);
          this.digits = Array(6).fill('');
          this.clearError();
          this.focusFirstInput();
        }
      } catch (error) {
        const errorResponse = error.response?.data;
        if (errorResponse?.status === 429 || errorResponse?.statusCode === 429) {
          this.isLockedOut = true;
          this.lockoutSeconds = this.parseLockoutDuration(errorResponse?.message || '60 seconds');
          this.otpErrorFlag = false;
          this.errorMsg = '';
        } else {
          helper.infoMessage(this, errorResponse?.status || 'error', errorResponse?.message || 'Failed to send OTP');
        }
      } finally {
        this.otpLoader = false;
      }
    },
    handleLockoutExpired() {
      console.log('Lockout expired at:', new Date().toISOString());
      this.isLockedOut = false;
      this.lockoutSeconds = 0;
      this.otpErrorFlag = false;
      this.errorMsg = '';
      this.digits = Array(6).fill('');
      this.focusFirstInput();
    }
  }
};
</script>

<style scoped>
.otp-input-wrapper {
  gap: 25px;
  margin: 10px 0;
}
.auth-form .otp-input-wrapper input.otp-input {
  height: 53px;
  font-size: 20px;
  text-align: center;
  border: 1px solid #bbb;
  border-radius: 8px;
}
.highlight_otp {
  border: 1px solid red !important;
}
.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
